import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { ManagedWindow, closeWindow } from "src/WindowManager/WindowState";
import { Spinner } from "react-bootstrap";
import { Document, Page } from "react-pdf";
import { useEffect, useState } from "react";
import { PDFDocument, rgb } from "pdf-lib";
import { MITDocument, MITDocumentVecMap, MITDocumentNextPage, MITDocumentSquare, MITDocumentTable, MITDocumentText, MITDocumentTitle, MITDocumentTile, MITDocumentAbsolute } from "src/utils/MITDocuments";
import { MitStorage } from "src/managers/MitStorage";
import { ViewButtonIcon } from "src/propertyInfoTemplates/PropertyComponents";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { ExportData } from "src/managers/ExportData";
import { geomWithBuffer } from "./ConflictSearchCalculations";
import { WFSEndpointsType, ConflictProfile } from "./ConflictSearchDialog";

export function ConflictReportDialog({ window }: { window: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();

   const [showSpecificPage] = useState<number | null>(0);
   const [arrayBuffer, setArrayBuffer] = useState<ArrayBuffer | null>(null);
   const [blob, setBlob] = useState<Blob | null>(null);
   const [pdfPages, setPDFPages] = useState<number>(0);

   useEffect(() => {
      let resS = MitStorage.getValue("resultAndSearchData");
      generateConflictReport(
         (window?.windowParams?.conflictReportData as RASType) ?? resS,
         {
            backgroundQuality: 0,
            konflictTables: false,
            ...(window?.windowParams?.additionalData)
         }).then(({ document, pages }) => {
            setArrayBuffer(document);
            setBlob(new Blob([document], { type: "application/pdf" }));
            setPDFPages(pages);
         });
   }, [window?.windowParams?.conflictReportData]);

   useEffect(() => {
      if (window?.windowParams?.conflictReportData) {
         MitStorage.setValue("resultAndSearchData", window?.windowParams.conflictReportData);
      }
   }, [window?.windowParams?.conflictReportData]);

   return (
      <AdvancedDragModal
         PosDefault={{ left: "50%", top: "0px", width: "50%", height: "100%", transform: "translate(-50%, 0)" }}
         title={window?.windowParams?.additionalData?.customTitle ?? "Konfliktsøgningsrapport"}
         onClose={() => windowDispatch(closeWindow(window.id))}
         subHeaderContent={
            <>
               <ViewButtonIcon
                  title="Clear result test data"
                  onClick={() =>
                     MitStorage.clearValue("resultAndSearchData")
                  }
               >
                  <MIBIcon icon="debugging" />
               </ViewButtonIcon>
               <ViewButtonIcon
                  title="Download PDF"
                  onClick={() => {
                     if (blob) {
                        ExportData.downloadBlob("conflict_report.pdf", blob);
                     }
                  }}
               >
                  <MIBIcon icon="download" />
               </ViewButtonIcon>
            </>
         }
      >
         <ADModalBody>
            {arrayBuffer ?
               <Document file={arrayBuffer} className={"DatarapportPDFViewer"} >
                  {showSpecificPage ?
                     <Page pageIndex={showSpecificPage} key={showSpecificPage} />
                     : Array.from({ length: pdfPages }, (_, i) => i).map((pageIndex) => (
                        <Page pageIndex={pageIndex} key={pageIndex} />
                     ))}
               </Document>
               : <Spinner />}
         </ADModalBody>
      </AdvancedDragModal >
   );
}

async function generateConflictReport(
   searchData: RASType,
   options?: {
      customTitle?: string,
      konflictTables?: boolean,
      backgroundQuality?: number,
   }): Promise<{ pages: number, document: ArrayBuffer }> {
   let imageQuality = 1 // import.meta.env.DEV ? 0 : options?.backgroundQuality ?? 0;
   // MARK: Version
   let pdf = await PDFDocument.create();
   let document = new MITDocument(pdf);
   await document.embedFont("regular", "/Poppins-Regular.ttf");
   await document.embedFont("bold", "/Poppins-Bold.ttf");
   await document.embedFont("italic", "/Poppins-Italic.ttf");

   let buffer = searchData.searchParams.buffer
   // let searchData = (await MitStorage.getValue("resultAndSearchData") ?? resultAndSearchData) as typeof resultAndSearchData;
   // HEADER
   document.setTitle(options?.customTitle ?? "Konfliktsøgningsrapport");
   document.setMVersion(process.env.packageContentVersion ?? "dev");
   document.setDate(new Date());
   document.setPageNr("Side: {i} / {t}")

   // Coverpage
   document.add(new MITDocumentSquare(0, 150));
   document.add(new MITDocumentTitle(options?.customTitle ?? "Konfliktsøgningsrapport")
      .setTextSize(30)
   );
   document.add(new MITDocumentSquare(0, 5));
   document.add(new MITDocumentText("Genereret d. " + new Date().toLocaleString()).setLeftPadding(22));

   document.add(
      new MITDocumentAbsolute(0, 170, 595, 200).add(
         new MITDocumentTile("/images/backgrounds/2.png")
            .setHeight(100)
            .setOpacity(0.5)
            .setHueRotate(Math.random() * 360)
      )
   );

   document.add(new MITDocumentNextPage());
   // Indholdsfortegnelse

   // Søge området og søge parametre
   document.add(new MITDocumentTitle("Søgeområde").setTextSize(16));
   document.add(new MITDocumentSquare(0, 10));

   let searchArea: any[] = [];

   if (searchData.searchParams.geom) {

      searchData.searchParams.geom["pdfStyle"] = { borderColor: rgb(0, 0.6, 0.6), borderWidth: 2, borderOpacity: .8, size: 5 };
      if (buffer && buffer > 0) {
         let fc = geomWithBuffer(searchData.searchParams.geom, searchData.searchParams.buffer ?? 0);
         fc["pdfStyle"] = { borderColor: rgb(0, 0.6, 0.6), borderWidth: 3, borderOpacity: 1, borderDashArray: [3, 6] };
         fc["mapBounds"] = true;
         searchArea = [fc, searchData.searchParams.geom];
      } else {
         searchData.searchParams.geom["mapBounds"] = true;
         searchArea = [searchData.searchParams.geom];
      }

      document.add(new MITDocumentVecMap(searchArea)
         .setImage(imageQuality)
         .setSignature([
            { title: "Søgeområde", style: { borderColor: rgb(0, 0.6, 0.6), borderWidth: 2, borderOpacity: 0.7 } },
            ...(buffer ? [{ title: "Søgeområde buffer", style: { borderColor: rgb(0, 0.6, 0.6), borderWidth: 1, borderOpacity: 0.7, borderDashArray: [3, 6] } }] : [])
         ])
      );
   }

   document.add(new MITDocumentTitle("Oversigt over konflikter")
      .setTextSize(16)
   );
   document.add(new MITDocumentSquare(0, 10));
   let searchLayers = (searchData.searchParams.pages?.map((page) => page.layers).flat() || searchData.searchParams.layers)
   let konfliktOversigt = searchLayers.map((search, index) => {
      let conflict = searchData.results.find((result) => result.layer === search.layer && result.source === search.source);
      return {
         Nr: index + 1,
         Kilde: search.source,
         Lag: search.title ?? search.layer,
         Antal: formatAntal(conflict?.matchedCount ?? 0)
      }
   })
   document.add(
      new MITDocumentTable({
         data: konfliktOversigt,
         formatter: [(col, row) => {
            if (row === undefined || row === null) {
               return { font: "regular" }
            } else if (row.Antal == "-") {
               return { font: "italic" };
            } else {
               return { font: "bold" };
            }
         }]
      }));

   // Detaljerede sider for hver konflikt
   searchLayers.forEach((search, index) => {
      let result = searchData.results.find((result) => result.layer === search.layer && result.source === search.source);
      if (!result || result.matchedCount <= 0) {
         return;
      }
      addConflictPage(result, search, document, index);
   });

   return await document.save().then((pdfBytes) => {
      return { pages: pdf.getPageCount(), document: pdfBytes.buffer as ArrayBuffer };
   });

   function addConflictPage(result: RASType["results"][number], search: WFSEndpointsType | undefined, document: MITDocument, index: number) {
      if (!search) {
         document.add(new MITDocumentNextPage());
         document.add(new MITDocumentText(`Konflikt ${index + 1}: ${result.layer}`).setTextSize(16));
         document.add(new MITDocumentText("Der kunne ikke hentes data for dette lag, og derfor kan der ikke genereres en detaljeret side for denne konflikt. Dette skyldes sandsynligvis en fejl i forbindelse med hentning af data fra WFS-tjenesten for dette lag. Tjek evt. indstillingerne for dette lag og prøv igen."));
         return;
      }


      document.add(new MITDocumentNextPage());
      document.add(new MITDocumentText(`${index + 1} - ${search?.title || search.layer}`).setTextSize(16));

      document.add(new MITDocumentSquare(0, 10))

      document.add(new MITDocumentVecMap([...searchArea, result.conflict])
         .setImage(imageQuality)
         .setSignature([
            { title: "Søgeområde", style: { borderColor: rgb(0, 0.6, 0.6), borderWidth: 2, borderOpacity: 0.7 } },
            ...(buffer ? [{ title: "Søgeområde buffer", style: { borderColor: rgb(0, 0.6, 0.6), borderWidth: 1, borderOpacity: 0.7, borderDashArray: [3, 6] } }] : []),
            { title: search?.title || result.layer, style: { color: rgb(0.8, 0, 0), opacity: 0.5 } },
         ])
      );

      if (options?.konflictTables) {
         // document.add(new MITDocumentSquare(0, 10))
         document.add(new MITDocumentTable(result.conflict.features.map((feature) => feature.properties || {})));
      }
   }
}

function formatAntal(count: number): string {
   if (count > 0) {
      return count.toLocaleString("da-DK");
   } else if (count < 0) {
      return "fejl";
   }
   return "-"
}

export type RASType = {
   results: {
      source: string,
      layer: string,
      conflict: GeoJSON.FeatureCollection,
      intersect: GeoJSON.FeatureCollection | null,
      progress: number,
      err: string,
      matchedCount: number,
   }[],
   searchParams:
   ConflictProfile<WFSEndpointsType> & {
      feature: string,
      geom: GeoJSON.FeatureCollection,
      buffer: number,
   }
}
